import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import type {
  SemanticContextCompileResponse,
  SemanticContextMCPPolicyResponse,
  SemanticContextMCPReadinessResponse,
  SemanticContextOntologyResponse,
  SemanticContextOverviewResponse,
  OntologyGenerationSettingsResponse,
  OntologyGenerationStreamState,
  OntologySuggestionResponse,
  WorkspaceVerifiedQuery,
  WorkspaceVerifiedQueryCreate,
  WorkspaceVerifiedQueryType,
  WorkspaceVerifiedQueryUpdate,
} from '../../hooks/useWorkspaceSemanticContext';
import {
  mapWorkspaceOntologyToSemanticPayload,
  useRunWorkspaceSemanticReadinessSmoke,
  useWorkspaceSemanticSetupReadiness,
} from '../../hooks/useWorkspaceSemanticContext';
import type { Toast } from '../../stores/useToasts';
import { Button } from '../ui';
import { SemanticOntologyBuilderSurface } from './SemanticOntologyBuilderSurface';
import { WorkspaceContextPacksSection } from './WorkspaceContextPacksSection';
import { WorkspaceCorrectionsSection } from './WorkspaceCorrectionsSection';
import { WorkspaceMCPToolsSection } from './WorkspaceMCPToolsSection';
import { WorkspaceSemanticOverviewPage } from './WorkspaceSemanticOverviewPage';
import { WorkspaceSemanticSetupGuide } from './WorkspaceSemanticSetupGuide';
import { WorkspaceVerifiedQueriesSection } from './WorkspaceVerifiedQueriesSection';
import type { StructuredAnalyticsDraft, WorkspaceOntology } from './structuredAnalyticsConfig';

export type SemanticShellSurface = 'overview' | 'setup' | 'ontology' | 'verified_queries' | 'context_packs' | 'corrections' | 'mcp';
type SemanticContextToast = Pick<Toast, 'kind' | 'message'>;

export interface WorkspaceSemanticContextShellProps {
  workspaceId: number;
  workspaceName?: string;
  workspaceDescription?: string;
  draft: StructuredAnalyticsDraft;
  semanticContext?: SemanticContextOverviewResponse;
  workspaceConfig?: Record<string, unknown> | null;
  canChat?: boolean;
  unstructuredCount?: number;
  unstructuredContent?: React.ReactNode;
  unstructuredDocumentCount?: number;
  unstructuredChunkCount?: number;
  connectorsContent?: React.ReactNode;
  overviewEyebrow?: string;
  showConnectorsSurface?: boolean;
  showStructuredSurface?: boolean;
  showOntologySurface?: boolean;
  showContextPacksSurface?: boolean;
  showVerifiedQueriesSurface?: boolean;
  showCorrectionsSurface?: boolean;
  showMcpSurface?: boolean;
  onOpenChat?: () => void;
  onOpenWarehouseConnections: () => void;
  onOpenCatalogConnections: () => void;
  onOpenCatalogApplet?: () => void;
  onOpenWarehouseRegistry: () => void;
  onSaveStructuredScope: (tableIds: number[], warehouseConnectorId?: number | null) => Promise<void>;
  onSaveOntologyDraft: (payload: SemanticContextOntologyResponse) => Promise<unknown>;
  onCompileRuntimePack: () => Promise<SemanticContextCompileResponse>;
  onPublishRuntimePack?: (packId: number) => Promise<SemanticContextCompileResponse>;
  ontologyGenerationSettings?: OntologyGenerationSettingsResponse;
  ontologySuggestions?: OntologySuggestionResponse[];
  ontologyGenerationStream?: OntologyGenerationStreamState;
  isOntologySuggestionsLoading?: boolean;
  isDraftingOntologySuggestions?: boolean;
  onDraftOntologyWithAI?: () => Promise<unknown>;
  onApplyOntologySuggestion?: (suggestionId: number) => Promise<unknown>;
  onApplyOntologySuggestionsBatch?: (
    runId: number,
    suggestionIds: number[],
  ) => Promise<unknown>;
  onRejectOntologySuggestion?: (suggestionId: number) => Promise<unknown>;
  verifiedQueries?: WorkspaceVerifiedQuery[];
  isVerifiedQueriesLoading?: boolean;
  isDraftingVerifiedQuery?: boolean;
  isSavingVerifiedQuery?: boolean;
  isReviewingVerifiedQuery?: boolean;
  isDeletingVerifiedQuery?: boolean;
  onCreateVerifiedQuery?: (payload: WorkspaceVerifiedQueryCreate) => Promise<unknown>;
  onDraftVerifiedQueryWithAI?: (payload: { query_type: WorkspaceVerifiedQueryType; business_question: string }) => Promise<unknown>;
  onUpdateVerifiedQuery?: (queryId: number, payload: WorkspaceVerifiedQueryUpdate) => Promise<unknown>;
  onApproveVerifiedQuery?: (queryId: number) => Promise<unknown>;
  onRejectVerifiedQuery?: (queryId: number) => Promise<unknown>;
  onDeleteVerifiedQuery?: (queryId: number) => Promise<unknown>;
  onNotify: (toast: SemanticContextToast) => void;
  mcpPolicy?: SemanticContextMCPPolicyResponse;
  mcpReadiness?: SemanticContextMCPReadinessResponse;
  isMcpPolicyLoading?: boolean;
  isMcpReadinessLoading?: boolean;
  mcpEnabled?: boolean;
  mcpAdminAllowed?: boolean;
  mcpToolSupport?: Record<string, boolean>;
  canManageMcpPolicy?: boolean;
  canUpdateWorkspace?: boolean;
  onToggleMcp?: (enabled: boolean) => void;
  isUpdatingMcp?: boolean;
  activeSurface?: SemanticShellSurface;
  onSurfaceChange?: (surface: SemanticShellSurface) => void;
}

const SURFACE_HEADERS: Partial<Record<SemanticShellSurface, {
  eyebrow: string;
  title: string;
  description: string;
}>> = {
  setup: {
    eyebrow: 'Semantic context / Guided setup',
    title: 'Setup guide',
    description:
      'Follow the backend readiness checks for warehouse scope, catalog coverage, ontology review, Runtime Context Pack activation, and MCP exposure.',
  },
  ontology: {
    eyebrow: 'Semantic context / Semantic ontology',
    title: 'Semantic ontology',
    description:
      'Model the governed workspace graph from business entities, metrics, and approved join paths before generating a Runtime version.',
  },
  context_packs: {
    eyebrow: 'Semantic context / Runtime artifact',
    title: 'Runtime versions',
    description:
      'Generated artifacts runtime queries use, bundling structured assets, retrieval collections, ontology, and guardrails.',
  },
  verified_queries: {
    eyebrow: 'Semantic context / Verified queries',
    title: 'Verified queries',
    description:
      'Review workspace-approved business question examples before they generate into Runtime versions.',
  },
  corrections: {
    eyebrow: 'Semantic context / Corrections raised',
    title: 'Corrections raised',
    description:
      'Review governed feedback signals that should become metadata coverage, ontology edits, or future runtime policy changes.',
  },
  mcp: {
    eyebrow: 'Admin / External access',
    title: 'MCP & external access',
    description:
      'Expose backend-approved workspace context to external MCP clients using the same active Runtime Context Pack and workspace policy.',
  },
};

function workspaceConfigWarehouseConnectorId(workspaceConfig?: Record<string, unknown> | null): number | undefined {
  const structuredAnalytics = workspaceConfig?.structured_analytics;
  if (!structuredAnalytics || typeof structuredAnalytics !== 'object') {
    return undefined;
  }

  const rawId = (structuredAnalytics as { warehouse_connector_id?: unknown }).warehouse_connector_id;
  if (typeof rawId === 'number') {
    return Number.isInteger(rawId) ? rawId : undefined;
  }
  if (typeof rawId === 'string') {
    const parsed = Number(rawId);
    return Number.isInteger(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function resolveWorkspaceSemanticConnectorId(
  semanticContext?: SemanticContextOverviewResponse,
  workspaceConfig?: Record<string, unknown> | null,
): number | undefined {
  return (
    semanticContext?.structured_scope.warehouse_connector?.id ??
    workspaceConfigWarehouseConnectorId(workspaceConfig)
  );
}

export function resolveWorkspaceSemanticSelectedTableIds(
  semanticContext?: SemanticContextOverviewResponse,
): number[] {
  return semanticContext?.structured_scope.selected_table_ids ?? [];
}

export function WorkspaceSemanticContextShell({
  workspaceId,
  workspaceName = 'Workspace',
  workspaceDescription = 'Configure how Agent Mesh retrieves from connected sources and how this workspace handles knowledge, ingestion, and chat.',
  draft,
  semanticContext,
  workspaceConfig,
  canChat,
  unstructuredCount,
  unstructuredContent,
  unstructuredDocumentCount,
  unstructuredChunkCount,
  connectorsContent,
  overviewEyebrow,
  showConnectorsSurface,
  showStructuredSurface = true,
  showOntologySurface = true,
  showContextPacksSurface = true,
  showVerifiedQueriesSurface = true,
  showCorrectionsSurface = true,
  showMcpSurface = true,
  onOpenChat,
  onOpenWarehouseConnections,
  onOpenCatalogConnections,
  onOpenCatalogApplet,
  onOpenWarehouseRegistry,
  onSaveStructuredScope,
  onSaveOntologyDraft,
  onCompileRuntimePack,
  onPublishRuntimePack,
  ontologyGenerationSettings,
  ontologySuggestions = [],
  ontologyGenerationStream,
  isOntologySuggestionsLoading = false,
  isDraftingOntologySuggestions = false,
  onDraftOntologyWithAI,
  onApplyOntologySuggestion,
  onApplyOntologySuggestionsBatch,
  onRejectOntologySuggestion,
  verifiedQueries = [],
  isVerifiedQueriesLoading = false,
  isDraftingVerifiedQuery = false,
  isSavingVerifiedQuery = false,
  isReviewingVerifiedQuery = false,
  isDeletingVerifiedQuery = false,
  onCreateVerifiedQuery,
  onDraftVerifiedQueryWithAI,
  onUpdateVerifiedQuery,
  onApproveVerifiedQuery,
  onRejectVerifiedQuery,
  onDeleteVerifiedQuery,
  onNotify,
  mcpPolicy,
  mcpReadiness,
  isMcpPolicyLoading = false,
  isMcpReadinessLoading = false,
  mcpEnabled,
  mcpAdminAllowed,
  mcpToolSupport,
  canManageMcpPolicy = false,
  canUpdateWorkspace = true,
  onToggleMcp,
  isUpdatingMcp = false,
  activeSurface: controlledActiveSurface,
  onSurfaceChange,
}: WorkspaceSemanticContextShellProps) {
  const [localActiveSurface, setLocalActiveSurface] = useState<SemanticShellSurface>('overview');
  const [isCompilingRuntimePack, setIsCompilingRuntimePack] = useState(false);
  const [publishingRuntimePackId, setPublishingRuntimePackId] = useState<number | null>(null);
  const [compileRuntimePackError, setCompileRuntimePackError] = useState<string | null>(null);
  const [compileRuntimePackSuccess, setCompileRuntimePackSuccess] = useState<string | null>(null);
  const [savedDraftNeedsCandidate, setSavedDraftNeedsCandidate] = useState(false);
  const lastOntologyGenerationTerminalEventRef = useRef<number | null>(null);
  const activeSurface = controlledActiveSurface ?? localActiveSurface;
  const latestRuntimePack = semanticContext?.readiness.latest_pack ?? null;
  const hasCurrentCandidate = Boolean(latestRuntimePack && !latestRuntimePack.is_active && latestRuntimePack.status === 'candidate');
  const draftChangeCount = getSemanticDeltaTotal(semanticContext?.readiness.draft_delta);
  const hasPersistedOntologyDraft = Boolean(
    semanticContext &&
      !semanticContext.readiness.active_pack &&
      (
        semanticContext.ontology.entities.length > 0 ||
        semanticContext.ontology.metrics.length > 0 ||
        semanticContext.ontology.relationships.length > 0 ||
        (semanticContext.ontology.semantic_mappings?.length ?? 0) > 0
      ),
  );
  const hasReviewableRuntimeChanges =
    savedDraftNeedsCandidate ||
    draftChangeCount > 0 ||
    hasCurrentCandidate ||
    hasPersistedOntologyDraft;
  const setActiveSurface = useCallback(
    (surface: SemanticShellSurface) => {
      if (controlledActiveSurface === undefined) {
        setLocalActiveSurface(surface);
      }
      onSurfaceChange?.(surface);
    },
    [controlledActiveSurface, onSurfaceChange],
  );

  const connectorId = useMemo(
    () => resolveWorkspaceSemanticConnectorId(semanticContext, workspaceConfig),
    [semanticContext, workspaceConfig],
  );
  const selectedTableIds = useMemo(
    () => resolveWorkspaceSemanticSelectedTableIds(semanticContext),
    [semanticContext],
  );
  const setupReadinessQuery = useWorkspaceSemanticSetupReadiness(workspaceId, showStructuredSurface);
  const readinessSmokeMutation = useRunWorkspaceSemanticReadinessSmoke(workspaceId);

  const scrollToWarehouseScope = useCallback(() => {
    setActiveSurface('overview');
    window.requestAnimationFrame(() => {
      document.getElementById('workspace-allowlist')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [setActiveSurface]);

  const scrollToCatalogCoverage = useCallback(() => {
    setActiveSurface('overview');
    window.requestAnimationFrame(() => {
      document.getElementById('workspace-catalog-coverage')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [setActiveSurface]);

  const saveOntologyDraft = useCallback(
    async (nextOntology: WorkspaceOntology) => {
      try {
        await onSaveOntologyDraft(
          mapWorkspaceOntologyToSemanticPayload(nextOntology, draft.allowlistRows),
        );
        onNotify({ kind: 'success', message: 'Ontology draft saved' });
      } catch (err) {
        onNotify({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to save ontology',
        });
        throw err;
      }
      setSavedDraftNeedsCandidate(true);
    },
    [draft.allowlistRows, onNotify, onSaveOntologyDraft],
  );

  const compileRuntimePack = useCallback(async () => {
    setIsCompilingRuntimePack(true);
    setCompileRuntimePackError(null);
    setCompileRuntimePackSuccess(null);
    try {
      const response = await onCompileRuntimePack();
      setCompileRuntimePackSuccess(`Runtime version v${response.pack.version} is ready to publish`);
      onNotify({
        kind: 'success',
        message: `Runtime version v${response.pack.version} is ready to publish`,
      });
      setSavedDraftNeedsCandidate(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate Runtime version';
      setCompileRuntimePackError(message);
      onNotify({
        kind: 'error',
        message,
      });
    } finally {
      setIsCompilingRuntimePack(false);
    }
  }, [onCompileRuntimePack, onNotify]);

  const publishRuntimePack = useCallback(
    async (packId: number) => {
      if (!onPublishRuntimePack) return;
      setPublishingRuntimePackId(packId);
      setCompileRuntimePackError(null);
      setCompileRuntimePackSuccess(null);
      try {
        const response = await onPublishRuntimePack(packId);
        setCompileRuntimePackSuccess(`Runtime version v${response.pack.version} is live`);
        onNotify({
          kind: 'success',
          message: `Runtime version v${response.pack.version} is live`,
        });
        setSavedDraftNeedsCandidate(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to publish Runtime version';
        setCompileRuntimePackError(message);
        onNotify({ kind: 'error', message });
      } finally {
        setPublishingRuntimePackId(null);
      }
    },
    [onNotify, onPublishRuntimePack],
  );

  const draftOntologyWithAI = useCallback(async () => {
    if (!onDraftOntologyWithAI) return;
    try {
      const response = await onDraftOntologyWithAI();
      const counts = (response as { suggestion_counts?: Record<string, unknown> })?.suggestion_counts;
      const total = typeof counts?.total === 'number' ? counts.total : undefined;
      onNotify({
        kind: 'success',
        message: total != null
          ? `${total} ontology suggestion${total === 1 ? '' : 's'} ready for review`
          : 'Ontology generation started',
      });
    } catch (err) {
      onNotify({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to draft ontology suggestions',
      });
    }
  }, [onDraftOntologyWithAI, onNotify]);

  useEffect(() => {
    const terminalEvent = ontologyGenerationStream?.failure ?? ontologyGenerationStream?.completion;
    if (!terminalEvent || terminalEvent.id === lastOntologyGenerationTerminalEventRef.current) {
      return;
    }
    lastOntologyGenerationTerminalEventRef.current = terminalEvent.id;
    if (ontologyGenerationStream?.failure) {
      onNotify({
        kind: 'error',
        message: terminalEvent.message ?? 'Ontology generation failed',
      });
      return;
    }
    onNotify({
      kind: 'success',
      message: terminalEvent.message ?? 'Ontology suggestions ready for review',
    });
  }, [onNotify, ontologyGenerationStream?.completion, ontologyGenerationStream?.failure]);

  const applyOntologySuggestion = useCallback(
    async (suggestionId: number) => {
      if (!onApplyOntologySuggestion) return;
      try {
        await onApplyOntologySuggestion(suggestionId);
        onNotify({ kind: 'success', message: 'Suggestion applied to ontology draft' });
      } catch (err) {
        onNotify({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to apply suggestion',
        });
      }
    },
    [onApplyOntologySuggestion, onNotify],
  );

  const applyOntologySuggestionsBulk = useCallback(
    async (suggestionIds: number[]) => {
      if (suggestionIds.length === 0) return;
      const suggestionItems = Array.isArray(ontologySuggestions)
        ? ontologySuggestions
        : [];
      const selectedSuggestions = suggestionItems.filter((suggestion) =>
        suggestionIds.includes(suggestion.id));
      const runIds = new Set(selectedSuggestions.map((suggestion) => suggestion.run_id));
      try {
        if (onApplyOntologySuggestionsBatch) {
          if (selectedSuggestions.length !== suggestionIds.length || runIds.size !== 1) {
            throw new Error('Selected suggestions must belong to one generation run');
          }
          await onApplyOntologySuggestionsBatch(
            selectedSuggestions[0].run_id,
            suggestionIds,
          );
        } else if (onApplyOntologySuggestion) {
          for (const suggestionId of suggestionIds) {
            await onApplyOntologySuggestion(suggestionId);
          }
        } else {
          return;
        }
      } catch (err) {
        onNotify({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to apply suggestions',
        });
        return;
      }
      onNotify({
        kind: 'success',
        message: `Applied ${suggestionIds.length} suggestion${suggestionIds.length === 1 ? '' : 's'} to ontology draft`,
      });
    },
    [
      onApplyOntologySuggestion,
      onApplyOntologySuggestionsBatch,
      onNotify,
      ontologySuggestions,
    ],
  );

  const rejectOntologySuggestion = useCallback(
    async (suggestionId: number) => {
      if (!onRejectOntologySuggestion) return;
      try {
        await onRejectOntologySuggestion(suggestionId);
        onNotify({ kind: 'warning', message: 'Suggestion rejected' });
      } catch (err) {
        onNotify({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to reject suggestion',
        });
      }
    },
    [onNotify, onRejectOntologySuggestion],
  );

  const openOntologyEditor = useCallback(() => {
    setActiveSurface('ontology');
  }, [setActiveSurface]);

  const openRuntimePackReview = useCallback(() => {
    setActiveSurface('context_packs');
  }, [setActiveSurface]);

  const reviewRuntimePackDraft = useCallback(async () => {
    setActiveSurface('context_packs');
    if (savedDraftNeedsCandidate || !hasCurrentCandidate) {
      await compileRuntimePack();
    }
  }, [compileRuntimePack, hasCurrentCandidate, savedDraftNeedsCandidate, setActiveSurface]);

  const renderPlaceholderSurface = (
    title: string,
    description: string,
    action?: React.ReactNode,
  ) => (
    <section className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
        <Squares2X2Icon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-charcoal dark:text-gray-100">{title}</h3>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
        {description}
      </p>
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </section>
  );

  const renderOntologyEditor = () => (
      <SemanticOntologyBuilderSurface
        workspaceId={workspaceId}
        draft={draft}
        semanticContext={semanticContext}
        generationSettings={ontologyGenerationSettings}
        suggestions={ontologySuggestions}
        generationStream={ontologyGenerationStream}
        isSuggestionsLoading={isOntologySuggestionsLoading}
        isDraftingSuggestions={isDraftingOntologySuggestions}
        onOpenOverview={() => setActiveSurface('overview')}
        onSaveOntologyDraft={saveOntologyDraft}
        onReviewDraft={reviewRuntimePackDraft}
        hasReviewableRuntimeChanges={hasReviewableRuntimeChanges}
        onDraftWithAI={draftOntologyWithAI}
        onApplySuggestion={applyOntologySuggestion}
        onApplySuggestionsBulk={applyOntologySuggestionsBulk}
        onRejectSuggestion={rejectOntologySuggestion}
      />
  );

  const renderOverviewSurface = () => (
    <WorkspaceSemanticOverviewPage
      workspaceId={workspaceId}
      workspaceName={workspaceName}
      workspaceDescription={workspaceDescription}
      draft={draft}
      semanticContext={semanticContext}
      connectorId={connectorId}
      selectedTableIds={selectedTableIds}
      canChat={canChat}
      unstructuredCount={unstructuredCount}
      unstructuredContent={unstructuredContent}
      unstructuredDocumentCount={unstructuredDocumentCount}
      unstructuredChunkCount={unstructuredChunkCount}
      connectorsContent={connectorsContent}
      overviewEyebrow={overviewEyebrow}
      showConnectorsSurface={showConnectorsSurface}
      showStructuredSurface={showStructuredSurface}
      showOntologyAffordances={showOntologySurface}
      showContextPackAffordances={showContextPacksSurface}
      onOpenChat={onOpenChat}
      onOpenWarehouseConnections={onOpenWarehouseConnections}
      onOpenCatalogConnections={onOpenCatalogConnections}
      onOpenCatalogApplet={onOpenCatalogApplet}
      onOpenWarehouseRegistry={onOpenWarehouseRegistry}
      onOpenOntology={openOntologyEditor}
      onOpenContextPacks={openRuntimePackReview}
      onOpenMcp={showMcpSurface ? () => setActiveSurface('mcp') : undefined}
      onOpenSetupGuide={() => setActiveSurface('setup')}
      setupReadiness={setupReadinessQuery.data}
      isSetupReadinessLoading={setupReadinessQuery.isLoading}
      isSetupReadinessError={setupReadinessQuery.isError}
      onSaveStructuredScope={onSaveStructuredScope}
      onNotify={onNotify}
    />
  );

  const renderOntologySurface = () => {
    return renderOntologyEditor();
  };

  const renderContextPacksSurface = () => {
    return (
      <div className="space-y-6">
        <WorkspaceContextPacksSection
          workspaceId={workspaceId}
          draft={draft}
          semanticContext={semanticContext}
          isCompiling={isCompilingRuntimePack}
          compileError={compileRuntimePackError}
          compileSuccess={compileRuntimePackSuccess}
          publishingPackId={publishingRuntimePackId}
          onPublishPack={onPublishRuntimePack ? publishRuntimePack : undefined}
        />
      </div>
    );
  };

  const renderVerifiedQueriesSurface = () => {
    if (
      onCreateVerifiedQuery &&
      onDraftVerifiedQueryWithAI &&
      onUpdateVerifiedQuery &&
      onApproveVerifiedQuery &&
      onRejectVerifiedQuery &&
      onDeleteVerifiedQuery
    ) {
      return (
        <WorkspaceVerifiedQueriesSection
          queries={verifiedQueries}
          isLoading={isVerifiedQueriesLoading}
          isDrafting={isDraftingVerifiedQuery}
          isSaving={isSavingVerifiedQuery}
          isReviewing={isReviewingVerifiedQuery}
          isDeleting={isDeletingVerifiedQuery}
          onCreate={onCreateVerifiedQuery}
          onDraftWithAI={onDraftVerifiedQueryWithAI}
          onUpdate={onUpdateVerifiedQuery}
          onApprove={onApproveVerifiedQuery}
          onReject={onRejectVerifiedQuery}
          onDelete={onDeleteVerifiedQuery}
        />
      );
    }

    return renderPlaceholderSurface(
      'Verified query authoring is not available in this route',
      'Workspace examples compile through Runtime Context Packs when the page receives the verified query API hooks.',
    );
  };

  const renderCorrectionsSurface = () => (
    <div className="space-y-6">
      <WorkspaceCorrectionsSection
        workspaceId={workspaceId}
        draft={draft}
        onOpenCatalogConnections={onOpenCatalogConnections}
      />
    </div>
  );

  const renderMcpSurface = () => {
    if (
      typeof mcpEnabled === 'boolean' &&
      typeof mcpAdminAllowed === 'boolean' &&
      onToggleMcp
    ) {
      return (
        <WorkspaceMCPToolsSection
          draft={draft}
          workspaceId={workspaceId}
          mcpEnabled={mcpEnabled}
          mcpAdminAllowed={mcpAdminAllowed}
          mcpPolicy={mcpPolicy}
          mcpReadiness={mcpReadiness}
          mcpToolSupport={mcpToolSupport}
          canManageMcpPolicy={canManageMcpPolicy}
          canUpdateWorkspace={canUpdateWorkspace}
          onToggleMcp={onToggleMcp}
          isUpdating={isUpdatingMcp}
          isPolicyLoading={isMcpPolicyLoading}
          isReadinessLoading={isMcpReadinessLoading}
        />
      );
    }

    return renderPlaceholderSurface(
      'MCP exposure controls are not available in this route',
      'The Workspace MCP surface will use the active Runtime Context Pack and workspace RBAC when MCP state is provided by the page.',
      <Button type="button" variant="outline" size="sm" onClick={() => setActiveSurface('context_packs')}>
        Review context pack
      </Button>,
    );
  };

  const renderSetupSurface = () => (
    <WorkspaceSemanticSetupGuide
      readiness={setupReadinessQuery.data}
      isLoading={setupReadinessQuery.isLoading}
      isError={setupReadinessQuery.isError}
      onOpenWarehouseConnections={onOpenWarehouseConnections}
      onOpenWarehouseScope={scrollToWarehouseScope}
      onOpenCatalogCoverage={scrollToCatalogCoverage}
      onOpenOntology={openOntologyEditor}
      onOpenContextPacks={openRuntimePackReview}
      onOpenMcp={showMcpSurface ? () => setActiveSurface('mcp') : undefined}
      onRunSmoke={() => readinessSmokeMutation.mutateAsync()}
      isSmokePending={readinessSmokeMutation.isPending}
      isSmokeError={readinessSmokeMutation.isError}
      profilingConnectorId={
        semanticContext?.structured_scope.warehouse_connector?.connector_type === 'bigquery'
          ? semanticContext.structured_scope.warehouse_connector.id
          : undefined
      }
    />
  );

  const renderActiveSurface = () => {
    if (activeSurface === 'setup' && showStructuredSurface) return renderSetupSurface();
    if (activeSurface === 'ontology' && showOntologySurface) return renderOntologySurface();
    if (activeSurface === 'verified_queries' && showVerifiedQueriesSurface) return renderVerifiedQueriesSurface();
    if (activeSurface === 'context_packs' && showContextPacksSurface) return renderContextPacksSurface();
    if (activeSurface === 'corrections' && showCorrectionsSurface) return renderCorrectionsSurface();
    if (activeSurface === 'mcp' && showMcpSurface) return renderMcpSurface();
    return renderOverviewSurface();
  };

  return (
    <div className={activeSurface === 'ontology' && showOntologySurface ? 'h-full' : 'space-y-6'}>
      {activeSurface !== 'overview' && !(activeSurface === 'ontology' && showOntologySurface) && (
        <SemanticSurfaceHeader surface={activeSurface} />
      )}

      {renderActiveSurface()}
    </div>
  );
}

function getSemanticDeltaTotal(delta: SemanticContextOverviewResponse['readiness']['draft_delta']): number {
  if (!delta) return 0;
  return typeof delta.total_changes === 'number' && Number.isFinite(delta.total_changes)
    ? delta.total_changes
    : Array.isArray(delta.changes) ? delta.changes.length : 0;
}

function SemanticSurfaceHeader({ surface }: { surface: SemanticShellSurface }) {
  const header = SURFACE_HEADERS[surface];
  if (!header) return null;

  return (
    <header className="flex flex-col gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {header.eyebrow}
      </p>
      <h1 className="font-title text-h1 leading-tight text-charcoal dark:text-gray-100">
        {header.title}
      </h1>
      <p className="max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
        {header.description}
      </p>
    </header>
  );
}
